import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  Checkbox,
} from '@mui/material';
import { Screen } from '../../components/Screen'; // Adjust the import path as needed
import SubPartnerInfo from './components/SubPartnerInfo';
import ProgressBar from './components/ProgressBar';
import ThemeOptions from './components/ThemeOptions';
import MainOptions from './components/MainOptions';
import LogosOptions from './components/LogosOptions';
import CustomizationOptions from './components/CustomizationOptions';
import SignUpOptions from './components/SignUpOptions';
import ThirdPartyAuthOptions from './components/ThirdPartyAuthOptions';
import WebhooksOptions from './components/WebhooksOptions';
import CompletionOptions from './components/CompletionOptions';
import ControlledAccordion from './components/ControlledAccordion';
import IsTangeloInfo from './components/IsTangeloInfo';
import { UserContext } from '../../lib/contexts/UserContext';

const OrganizationOnboardingScreen = () => {
  const [formData, setFormData] = useState({
    isSubPartner: false,
    isTangelo: false,
    subPartnerOf: '',
    primaryColor: '#196161',
    secondaryColor: '#E2A886',
    tertiaryColor: '',
    borderWidth: '',
    borderRadius: '',
    borderColor: '',
    boxShadow: '',
    iconOptions: '',
    subdomain: '',
    name: '',
    welcomeMessage: 'Welcome to Diet ID',
    assessmentMessage: '',
    idealMessage: '',
    challengesMessage: '',
    leadEmail: '',
    receivePHI: false,
    isFreeKiosk: false,
    logos: {
      small: '',
      medium: '',
      large: '',
      lightSmall: '',
      lightMedium: '',
      lightLarge: '',
      favicon: '',
    },
    customizationOptions: {
      useMetricSystem: false,
      showPoweredBy: false,
      skipHowItWorks: false,
      customMealPlans: false,
      showCODEQ: false,
      displayDIEMScores: false,
    },
    signUpOptions: {
      enableSignUp: false,
      hideEmail: false,
      hidePhone: false,
      enableCodeBasedLogin: false,
      requirePasswordExpiration: false,
    },
    thirdPartyAuth: {
      virginPulseLogin: false,
      epicLogin: false,
      cerboProviderID: '',
    },
    webhooks: {
      ssoUrl: '',
      userSignupWebhookUrl: '',
      firehoseWebhookUrl: '',
      webhookApiKey: '',
      patientNoteWebhookUrl: '',
      workflowWebhookUrl: '',
      workflowWebhookVersion: 'All',
    },
    completionOptions: {
      showShareWidget: false,
      showExtendedShareWidget: false,
      finishButtonLabel: 'Finish',
      signOutOnCompletion: false,
      externalLinkOnCompletion: '',
      showCompletionScreen: false,
      completionScreenContent: '',
    },
    screenerOptions: {
      showScreenerImages: false,
      suppressSpecificDietSelection: false,
      suppressDietStyleSelection: false,
      suppressHealthGoalSelection: false,
    },
    personalizationOptions: {
      showBMI: false,
      hidePregnancySelection: false,
      collectPostalCode: false,
      collectDateOfBirth: false,
      collectExtendedUserInfo: false,
      customField: '',
      enablePediatricWorkflows: false,
      receiveEngagementEmail: false,
    },
    idOptions: {
      enableSimpleScreener: false,
      showTherapeuticOptions: false,
      bypassIDResults: false,
      bypassIdealResults: false,
      bypassIDvsIdealResults: false,
      askForUserAccuracyRatingID: false,
      askForUserAccuracyRatingFullFlow: false,
    },
    idealOptions: {
      disableIdeal: false,
      allowSkipIdeal: false,
      assignAlgorithmicIdeal: false,
      askForUserAccuracyRatingIdeal: false,
      askForUserAccuracyRatingFullFlow: false,
    },
    dietaryCustomizations: {
      nutrientsToShow: [],
      goalsToShow: [],
      idealDietsToShow: [],
    },
  });

  const [tempTheme, setTempTheme] = useState({});

  const [scrollProgress, setScrollProgress] = useState(0);
  const [sections, setSections] = useState([
    { name: 'Theme Options', open: false },
    { name: 'Main Options', open: false },
    { name: 'Logos', open: false },
    { name: 'Customization Options', open: false },
    { name: 'Sign Up Options', open: false },
    { name: '3rd-Party Authentication Options', open: false },
    { name: 'Webhooks Options', open: false },
    { name: 'Completion Options', open: false },
  ]);

  const accordionRefs = sections.map(() => React.createRef());

  const {
    state: { user },
  } = useContext(UserContext);

  const handleAccordionChange = (sectionName) => (event, isExpanded) => {
    setSections((prevSections) =>
      prevSections.map((section) => (section.name === sectionName ? { ...section, open: isExpanded } : section)),
    );
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate form and submit data
    // console.log('Form data submitted:', formData);
  };

  const handleSectionClick = (index) => {
    accordionRefs[index].current.scrollIntoView({ behavior: 'smooth' });
    setSections((prevSections) =>
      prevSections.map((section, i) => (i === index ? { ...section, open: true } : section)),
    );
  };

  const isMobile = window.innerWidth <= 768;
  let isSignedIn;
  if (user) {
    isSignedIn = true;
  } else {
    isSignedIn = false;
  }

  return (
    <Screen
      title="Organization Onboarding"
      showSidebarNav={true}
      admin={true}
      showMainMenu={isSignedIn}
    >
      <Box
        sx={{ display: 'flex', padding: 1 }}
        id={'nani'}
      >
        <Box sx={{ flex: 1, marginRight: '220px' }}>
          {/* Sticky Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              backgroundColor: '#f3f3f6',
              padding: 2,
              marginBottom: 2,
              width: isMobile ? '130%' : '100%',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                marginBottom: 2,
                paddingLeft: !isSignedIn && isMobile ? '10%' : '0',
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
              Onboard New Enterprise Partner
            </Typography>
          </Box>
          {/* Rest of the form */}
          <Box
            component="form"
            sx={{ padding: 2, marginBottom: 2, width: isMobile ? '130%' : '100%' }}
            onSubmit={handleSubmit}
          >
            {/* Sub-Partner Information */}
            <Grid
              container
              spacing={2}
              mb={2}
              style={{ paddingLeft: !isSignedIn && isMobile ? '10%' : '0' }}
            >
              <Grid
                item
                xs={6}
              >
                <SubPartnerInfo
                  formData={formData}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid
                item
                xs={6}
                container
                justifyContent="flex-end"
              >
                <IsTangeloInfo
                  formData={formData}
                  handleChange={handleChange}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
            >
              {sections.map((section, index) => (
                <Grid
                  item
                  xs={12}
                  key={index}
                  ref={accordionRefs[index]}
                  id={'sumbs'}
                  style={{ minWidth: !isSignedIn && isMobile ? '115%' : '100%' }}
                >
                  <ControlledAccordion
                    title={section.name}
                    description={!isMobile ? `Configure the ${section.name.toLowerCase()} for your organization` : ''}
                    contents={
                      {
                        'Theme Options': (
                          <ThemeOptions
                            formData={formData}
                            handleChange={handleChange}
                            setTempTheme={setTempTheme}
                          />
                        ),
                        'Main Options': (
                          <MainOptions
                            formData={formData}
                            handleChange={handleChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        Logos: (
                          <LogosOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Customization Options': (
                          <CustomizationOptions
                            formData={formData}
                            handleChange={handleChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Sign Up Options': (
                          <SignUpOptions
                            formData={formData}
                            handleChange={handleChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Third-Party Authentication Options': (
                          <ThirdPartyAuthOptions
                            formData={formData}
                            handleChange={handleChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Webhooks Options': (
                          <WebhooksOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Completion Options': (
                          <CompletionOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                            tempTheme={tempTheme}
                          />
                        ),
                      }[section.name]
                    }
                    expanded={section.open}
                    onChange={handleAccordionChange(section.name)}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Submit Button */}
            <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
        {!isMobile && (
          <Box sx={{ position: 'fixed', top: '20%', right: '2%', width: '200px', zIndex: 1000 }}>
            <ProgressBar
              sections={sections}
              onSectionClick={handleSectionClick}
            />
          </Box>
        )}
      </Box>
    </Screen>
  );
};

export default OrganizationOnboardingScreen;
